import Link from '@mui/icons-material/Link'
import { Tooltip } from '@mui/material'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  RedditIcon,
  RedditShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon,
} from 'react-share'
import eyesLookingDown from 'src/assets/eyes_looking_down.png'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { SetupContext } from 'src/consumer/context/SetupContext'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import {
  SEOComponent,
  calculateReadTimeFromWords,
  countWordsInHTML,
  formatDateForPosts,
  getParseDataFromHtmlString,
} from 'src/consumer/helpers/utilFunctions'
import {
  getAllCoursesCategoryItemsForPublic,
  getDataWithoutParams,
  getFeatureItemBySlug,
  getFeatureList,
  postDataWithoutParams,
} from 'src/consumer/services/featureServices'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { GenericDisplayFlex, GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { isUserAllowedToAccessContent } from '../ActivityPlansPage/activityPlanHelpers'
import { LoadingSpinner } from '../Loaders'
import PrewallAndPaywall from '../PrewallAndPaywall/PrewallAndPaywall'
import { getDisplayEventTypesOfMentor } from '../Professionals List Page'
import { PublicAppFooter } from '../PublicAppFooter'
import PublicAppHeader from '../PublicAppHeader'
import {
  getArrayOfLanguagesOfMentor,
  getYearsOfExperienceFromProfile,
} from '../ServiceProvidersListingPage/ServiceProvidersListingComponents'
import { ClubbedAuthModal } from '../SignIn/ClubbedAuthComponents'
import { ArticleLayoutCardComponent } from '../Tile/components/layoutCards/ArticleLayoutCard'
import { SquareMidCardSingleCard } from '../Tile/components/layoutCards/SquareMidCard'
import { MentorCardPortraitSingleCard } from '../Tile/components/layoutCards/mentorCardPortrait/MentorCardPortrait'
import { APArticleBlur } from './ArticlePageComponents'
import {
  APArticleContainer,
  APArticleDescription,
  APArticleHeading,
  APArticleMainImage,
  APArticleTextContainer,
  APCreatorImage,
  APRelatedArticleContainer,
  APRelatedArticlesSeparator,
  APShortDetailsSection,
  APShortDetailsText,
  RelatedArticlesHeading,
} from './ArticlePageStyles'

export default function ArticlePage() {
  const navigate = useNavigate()
  const locationData = useLocation()

  const { slug, articleId } = useParams()
  const { profileData } = useContext(ProfileContext)
  const { setupData } = useContext(SetupContext)

  const slugOrArticle = slug || articleId

  const { state } = useLocation()
  // const reduxState = useSelector((state) => state)

  const articleEndingRef = useRef(null)
  const urlParams = new URLSearchParams(window.location.search)
  const { unSecureSetupData } = useContext(UnsecureSetupContext)

  const [dataLoading, setDataLoading] = useState(false)
  const [articleData, setArticleData] = useState()
  const [relatedArticlesItems, setRelatedArticlesItems] = useState([])
  const [relatedTherapistItems, setRelatedTherapistItems] = useState([])
  const [relatedMeditationCourses, setRelatedMeditationCourses] = useState([])
  const [relatedJournalItems, setRelatedJournalItems] = useState([])
  const [prewallOpen, setPrewallOpen] = useState(false)
  const [showClubbedAuthModal, setShowClubbedAuthModal] = useState(false)
  const [typeOfClubbedAuthModal, setTypeOfClubbedAuthModal] = useState('login')

  const relatedArticleItemsToUse = useMemo(() => {
    return relatedArticlesItems?.filter((article) =>
      slug ? article?.slug !== slug : article?.assessmentId !== articleId,
    )
  }, [relatedArticlesItems])

  async function getNextPageOfRelatedArticles(RcvdArticleData) {
    const relatedArticlesResponse = await getFeatureList('article')
    if (relatedArticlesResponse.status === 200) {
      const relatedItemsArray = relatedArticlesResponse?.data || []
      setRelatedArticlesItems(
        relatedItemsArray.filter((item) => item?.assessmentId !== RcvdArticleData?.id).slice(0, 2),
      )
    }
  }

  async function getNextPageOfRelatedTherapists() {
    const relatedTherapistsResponse = await getDataWithoutParams(
      '/api/external/mentor/1on1/list?categories=therapist',
    )
    if (relatedTherapistsResponse.status === 200) {
      const relatedItemsArray = relatedTherapistsResponse?.data || []
      setRelatedTherapistItems(relatedItemsArray.slice(0, 2))
    }
  }

  async function getNextPageOfRelatedMeditationCourses() {
    const relatedCoursesResponse = await getAllCoursesCategoryItemsForPublic(1, 'meditation')
    if (relatedCoursesResponse.status === 200) {
      const relatedItemsArray = relatedCoursesResponse?.data || []
      setRelatedMeditationCourses(relatedItemsArray.slice(0, 2))
    }
  }

  async function getNextPageOfRelatedJournals() {
    const relatedJournalsResponse = await getFeatureList('journal', 1)
    if (relatedJournalsResponse.status === 200) {
      const relatedItemsArray = relatedJournalsResponse?.data || []
      setRelatedJournalItems(relatedItemsArray.slice(0, 2))
    }
  }

  async function pageSetup() {
    setDataLoading(true)
    setRelatedArticlesItems([])

    let response
    if (state?.passedArticleData) {
      response = { status: 200, data: state?.passedArticleData }
      window.history.replaceState({}, '')
    } else if (slug) {
      response = await getFeatureItemBySlug('article', slug, true)
    } else {
      response = await getFeatureItemBySlug('article', articleId, false)
    }
    if (response?.status === 200 && response?.data) {
      setArticleData(response?.data?.data?.article)
      setDataLoading(false)
      getNextPageOfRelatedArticles(response?.data?.data?.article)
      getNextPageOfRelatedTherapists()
      getNextPageOfRelatedMeditationCourses()
      getNextPageOfRelatedJournals()
      // if response says need to be logged in, then show login popup and bring back on this page
    } else {
      navigate('/404')
    }
  }

  async function markThisArticleCompleted() {
    let src, srcId
    if (locationData?.state?.source) {
      src = locationData.state.source
    } else {
      src = urlParams?.has('src') ? urlParams.get('src') : ''
    }
    if (locationData?.state?.id) {
      srcId = locationData.state.id
    } else {
      srcId = urlParams?.has('src-id') ? urlParams.get('src-id') : ''
    }

    if (articleData?.id && src && srcId) {
      await postDataWithoutParams(
        `/api/secure/article/read/completed?id=${articleData?.id}&src=${src}&src-id=${srcId}`,
        {},
      )
    }
  }

  useEffect(() => {
    // use intersection observer with articleEndingRef to fire API
    markThisArticleCompleted()
  }, [articleData])

  useEffect(() => {
    pageSetup()
  }, [slugOrArticle])

  // two api calls happening, stop that

  // can use fields  : subscription, slug, publishAt(type instant), featured, coverImage, coverIcon,
  // content(use this to find number of words and number of words, use 130wpm speed)
  // creator object to come : will have slug

  const titlePartsArray = articleData?.title?.split(':')

  let allowAccessToContent
  if (articleData?.subscription?.id && articleData?.subscription?.id !== 'subscription_free') {
    if (localStorage.getItem('token')) {
      if (
        isUserAllowedToAccessContent(profileData?.subscriptionTypeId, articleData?.subscription?.id)
      ) {
        allowAccessToContent = true
      } else {
        allowAccessToContent = false
      }
    } else {
      allowAccessToContent = false
    }
  } else {
    allowAccessToContent = true
  }

  return (
    <>
      {articleData && (
        <SEOComponent
          title={`${articleData?.title} | AUMHUM`}
          description={
            articleData?.description || 'Read articles covering vast mental wellness topics.'
          }
          image={articleData && getImageLink(articleData?.coverImage)}
        />
      )}
      {dataLoading ? (
        <LoadingSpinner bgColor="#F5F5F599" dark />
      ) : (
        <>
          <PublicAppHeader
            config={{ showLinks: true }}
            loginFunction={() => {
              setTypeOfClubbedAuthModal('login')
              setShowClubbedAuthModal(true)
            }}
            registerFunction={() => {
              setTypeOfClubbedAuthModal('signup')
              setShowClubbedAuthModal(true)
            }}
          />
          <APArticleContainer>
            {/* {reduxState?.articleGoBackState && (
              <GoBackToPageWhereYouCameFromButton onClick={reduxState?.articleGoBackState.onClick}>
                {reduxState?.articleGoBackState.text}
              </GoBackToPageWhereYouCameFromButton>
            )} */}
            {titlePartsArray?.length > 1 ? (
              <>
                <div>
                  <b>{titlePartsArray?.[0]?.trim()}</b>
                </div>
                <APArticleHeading>{titlePartsArray?.[1]?.trim()}</APArticleHeading>
              </>
            ) : (
              <APArticleHeading>{titlePartsArray?.[0]?.trim()}</APArticleHeading>
            )}
            <APArticleDescription>
              {articleData?.longDesc || articleData?.shortDesc}
            </APArticleDescription>
            <APShortDetailsSection>
              <APCreatorImage
                src={getImageLink(
                  articleData?.author?.coverIcon,
                  'MEDIA-e8bd40c2af6e4d49b4d2aef2613c3234',
                )} alt='articleCoverImg'
              />
              <GenericDisplayFlexColumn>
                <APShortDetailsText big>
                  {articleData?.author?.title || 'AUMHUM Staff'}
                </APShortDetailsText>
                <APShortDetailsText>
                  {`${
                    articleData?.content &&
                    calculateReadTimeFromWords(countWordsInHTML(articleData?.content))
                  } mins read · ${formatDateForPosts(articleData?.publishAt)}`}
                </APShortDetailsText>
              </GenericDisplayFlexColumn>
            </APShortDetailsSection>
            {(articleData?.blogBanner || articleData?.coverImage) && (
              <APArticleMainImage
                src={getImageLink(articleData?.blogBanner, articleData?.coverImage)} alt='articleImg'
              />
            )}

            <APArticleTextContainer premium={!allowAccessToContent}>
              {articleData?.content && getParseDataFromHtmlString(articleData?.content)}
              {!allowAccessToContent && (
                <APArticleBlur
                  openPrewall={() => setPrewallOpen(true)}
                  openAuthModal={(data) => {
                    setTypeOfClubbedAuthModal(data)
                    setShowClubbedAuthModal(true)
                  }}
                />
              )}
            </APArticleTextContainer>

            <div ref={articleEndingRef}></div>

            <GenericDisplayFlex style={{ gap: '10px', margin: '20px 0' }}>
              <span>Share: </span>
              <Tooltip title="Copy Link">
                <Link
                  sx={{ fontSize: '20px', cursor: 'pointer' }}
                  onClick={() => navigator.clipboard.writeText(window.location.href)}
                />
              </Tooltip>
              <WhatsappShareButton
                url={window.location.href}
                title={articleData?.title || ''}
                separator=":: "
              >
                <WhatsappIcon size={32} round />
              </WhatsappShareButton>
              <FacebookShareButton
                url={window.location.href}
                className="Demo__some-network__share-button"
              >
                <FacebookIcon size={32} round />
              </FacebookShareButton>
              <TwitterShareButton
                url={window.location.href}
                title={articleData?.title || ''}
                className="Demo__some-network__share-button"
              >
                <XIcon size={32} round />
              </TwitterShareButton>

              <LinkedinShareButton url={window.location.href}>
                <LinkedinIcon size={32} round />
              </LinkedinShareButton>
              <RedditShareButton
                url={window.location.href}
                title={articleData?.title || ''}
                windowWidth={660}
                windowHeight={460}
                className="Demo__some-network__share-button"
              >
                <RedditIcon size={32} round />
              </RedditShareButton>
            </GenericDisplayFlex>

            {/* <APMoreDetailsSection>
              <APCreatorImage
                big
                src={getImageLink(
                  articleData?.author?.coverIcon,
                  'MEDIA-e8bd40c2af6e4d49b4d2aef2613c3234',
                )}
              />
              <div>{articleData?.author?.description}</div>
            </APMoreDetailsSection> */}

            <APRelatedArticlesSeparator>
              <img src={eyesLookingDown} alt="eyes" />
            </APRelatedArticlesSeparator>
            <RelatedArticlesHeading to={'/article'}>Related Articles</RelatedArticlesHeading>

            <APRelatedArticleContainer>
              {relatedArticleItemsToUse?.map((rArticle) => (
                <ArticleLayoutCardComponent
                  key={rArticle.id}
                  lightMode
                  inFlexContainer
                  getActionButtonLogic={(item) => ({
                    text: 'Read',
                    onClick: (item) => {
                      navigate(`/article/${item?.slug}`)
                    },
                  })}
                  getMainImage={(item) => item?.coverImage || item?.coverIcon || item?.blogBanner}
                  getSecondaryText={(item) => item?.description}
                  item={rArticle}
                  getTitleField={(item) => item?.title}
                />
              ))}
            </APRelatedArticleContainer>

            <RelatedArticlesHeading to={'/therapy-psychiatry'}>
              Talk to an expert Therapist
            </RelatedArticlesHeading>
            <APRelatedArticleContainer>
              {relatedTherapistItems.map((item, index) => {
                return (
                  <MentorCardPortraitSingleCard
                    key={index}
                    item={item}
                    getTitleField={(item) => item?.name}
                    getMainImage={(item) => getImageLink(item?.profilePicId)}
                    getSecondaryText={(profile) => {
                      const exp = getYearsOfExperienceFromProfile(profile)
                      if (exp >= 8) {
                        return `${exp}+ years of experience`
                      }
                      return null
                    }}
                    getTertiaryText={(profile) => {
                      const languagesArrayData = getArrayOfLanguagesOfMentor(
                        profile,
                        unSecureSetupData,
                      )
                      if (languagesArrayData?.length > 0) {
                        return `Speaks: ${languagesArrayData?.join(', ')}`
                      }
                      return null
                    }}
                    getPrimarySpecialText={() => null}
                    getSecondarySpecialText={() => null}
                    getTertiarySpecialText={() => null}
                    getActionButtonLogic={(item) => ({
                      text: 'Book',
                      onClick: (item) => {
                        const displayEventTypes = getDisplayEventTypesOfMentor(item)
                        navigate(
                          `/mentor/${item?.slug}/events/${
                            displayEventTypes?.find((item) => !item?.free)?.uid
                          }`,
                        )
                      },
                    })}
                    getSecondaryActionButtonLogic={(item) => ({
                      text: 'Explore',
                      onClick: (item) => {
                        navigate(`/mentor/${item?.slug}/events`)
                      },
                    })}
                  />
                )
              })}
            </APRelatedArticleContainer>
            <RelatedArticlesHeading to={'/c/meditation'}>Guided Meditations</RelatedArticlesHeading>
            <APRelatedArticleContainer>
              {relatedMeditationCourses.map((item, index) => {
                return (
                  <SquareMidCardSingleCard
                    key={index}
                    item={item}
                    getTitleField={(item) => item?.title}
                    backgroundColor="#557183"
                    onClick={(item) => {
                      if (item?.slug) {
                        navigate(`/c/view/${item?.slug}`)
                      } else {
                        navigate(`/c/view/id/${item?.assessmentId}`)
                      }
                    }}
                    getMainImage={(item) => getImageLink(item?.coverIcon)}
                    getActionButtonLogic={() => ({
                      text: 'Explore',
                      onClick: (item) => {
                        if (item?.slug) {
                          navigate(`/c/view/${item?.slug}`)
                        } else {
                          navigate(`/c/view/id/${item?.assessmentId}`)
                        }
                      },
                    })}
                    getSecondaryText={() => {
                      return 'Guided Meditation'
                    }}
                  />
                )
              })}
            </APRelatedArticleContainer>
            <RelatedArticlesHeading to={'/journal'}>CBT Journals</RelatedArticlesHeading>
            <APRelatedArticleContainer>
              {relatedJournalItems.map((item, index) => {
                return (
                  <SquareMidCardSingleCard
                    key={index}
                    item={item}
                    getTitleField={(item) => item?.title}
                    backgroundColor="#557183"
                    onClick={(item) => {
                      if (item?.slug) {
                        navigate(`/journal/${item?.slug}`)
                      } else {
                        navigate(`/journal/id/${item?.assessmentId}`)
                      }
                    }}
                    getMainImage={(item) => getImageLink(item?.coverIcon)}
                    getActionButtonLogic={() => ({
                      text: 'Journal',
                      onClick: (item) => {
                        if (item?.slug) {
                          navigate(`/journal/${item?.slug}`)
                        } else {
                          navigate(`/journal/id/${item?.assessmentId}`)
                        }
                      },
                    })}
                    getSecondaryText={() => {
                      return 'Journal'
                    }}
                  />
                )
              })}
            </APRelatedArticleContainer>
          </APArticleContainer>
          <PrewallAndPaywall
            id="journal_play"
            secureData={setupData}
            wallIsOpen={prewallOpen}
            toggleWall={() => {
              setPrewallOpen((prev) => !prev)
            }}
          />
          <ClubbedAuthModal
            open={showClubbedAuthModal}
            onClose={() => setShowClubbedAuthModal(false)}
            loginVariant={typeOfClubbedAuthModal === 'login'}
          />
          <PublicAppFooter />
        </>
      )}
    </>
  )
}
